import React, { useState, useEffect } from 'react';
import {
  formatDateShort,
  formatCurrency,
  formatPercentage,
  formatNumber,
  calculateDaysUntilExpiration
} from '../../utils/screenerDataHelpers';
import { recalculateContract } from '../../services/api';
import { getIvTooltip } from '../../utils/dataSourceTooltip';
import '../../styles/MobileScreener.css';

/**
 * Individual contract card displayed in story view
 * Shows contract details, metrics, interactive sliders, and Greeks
 */
const ContractCard = ({ contract, expirationSettings, onGainUpdate, onUpdateAll, originalSettings }) => {
  // Extract contract data FIRST (before using in hooks)
  const ticker = contract.Ticker || contract.ticker || '';
  const optionType = contract['Option Type'] || contract.option_type || 'call';
  const strike = contract.Strike || contract.strike || 0;
  const currentPremium = contract['Current Premium'] || contract.current_premium || 0;
  const volume = contract.Volume || contract.volume || 0;
  const expiration = contract.Expiration || contract.expiration;

  const maxDaysUntilExp = calculateDaysUntilExpiration(expiration);

  // Get initial values from expiration-specific settings or contract data
  const initialSettings = expirationSettings[expiration] || expirationSettings['all'] || {};
  const [days, setDays] = useState(
    initialSettings.days || contract['Days to Gain'] || Math.floor(maxDaysUntilExp * 0.5)
  );
  const [move, setMove] = useState(
    initialSettings.gain || contract['Underlying Gain %'] || 10
  );

  // Simulated values (recalculated via Polygon API with EODHD fallback)
  const [simulatedGain, setSimulatedGain] = useState(
    contract['Premium % Gain'] || 0
  );
  const [simulatedPremium, setSimulatedPremium] = useState(
    contract['Simulated Premium'] || contract['Current Premium'] || 0
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Update mode: 'this' = single contract, 'all' = all contracts
  const [updateMode, setUpdateMode] = useState('this');

  // Update when expiration settings change
  useEffect(() => {
    const settings = expirationSettings[expiration] || expirationSettings['all'] || {};
    if (settings.days !== undefined) setDays(settings.days);
    if (settings.gain !== undefined) setMove(settings.gain);
  }, [expiration, expirationSettings]);

  // Manual recalculation via backend API (Polygon primary, EODHD fallback)
  const handleRecalculate = async () => {
    // If "All Contracts" mode is selected, use onUpdateAll callback
    if (updateMode === 'all' && onUpdateAll) {
      setLoading(true);
      setError(null);
      try {
        await onUpdateAll(days, move);
      } catch (err) {
        console.error('Error updating all contracts:', err);
        setError('Unable to update all contracts');
      } finally {
        setLoading(false);
      }
      return;
    }

    // Single contract update (original behavior)
    setLoading(true);
    setError(null);

    try {
      const response = await recalculateContract({
        ticker,
        option_type: optionType,
        strike,
        expiration,
        days_to_gain: days,
        stock_gain_pct: move / 100  // Convert from percentage to decimal
      });

      // Update simulated values from live Polygon/EODHD data
      const newGain = response.data.premium_gain_pct;
      const newPremium = response.data.simulated_premium;

      setSimulatedGain(newGain);
      setSimulatedPremium(newPremium);

      // Notify parent to update ticker bubble average
      if (onGainUpdate) {
        onGainUpdate(ticker, strike, expiration, newGain, newPremium);
      }

      // Log data source for monitoring (Polygon vs EODHD)
      if (process.env.NODE_ENV === 'development') {
        console.log(`[ContractCard] Recalculated ${ticker} using ${response.data.data_source}`);
      }
    } catch (err) {
      console.error('Error recalculating contract:', err);
      setError('Unable to update simulation');
    } finally {
      setLoading(false);
    }
  };

  // Reset to original queue settings
  const handleReset = () => {
    if (originalSettings) {
      setDays(originalSettings.days);
      setMove(originalSettings.gain);
    }
  };

  // Greeks
  const iv = contract['Implied Volatility'] || contract.implied_volatility || 0;
  const delta = contract.Delta || contract.delta || 0;
  const gamma = contract.Gamma || contract.gamma || 0;
  const theta = contract.Theta || contract.theta || 0;
  const vega = contract.Vega || contract.vega || 0;

  const gainClass = simulatedGain > 0 ? 'positive' : 'negative';
  const typeClass = optionType.toLowerCase() === 'call' ? 'badge-call' : 'badge-put';

  // Date formatting for slider value display (matching GlobalSimulationControls)
  const getTargetDate = (daysFromNow) => {
    const today = new Date();
    return new Date(today.getTime() + (daysFromNow * 24 * 60 * 60 * 1000));
  };

  const formatTargetDate = (numDays) => {
    const date = getTargetDate(numDays);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day} (${numDays}d)`;
  };

  // Format gain label with sign
  const formatGainLabel = (value) => {
    if (value === 0) return '0%';
    return value > 0 ? `+${value}%` : `${value}%`;
  };

  return (
    <div className="result-card-compact">
      {/* Main Info */}
      <div className="card-main-info">
        <div className="card-ticker-large">{ticker}</div>
        <span className={`card-type-badge ${typeClass}`}>
          {optionType.toUpperCase()}
        </span>
        <div className="card-expiry">Exp: {formatDateShort(expiration)}</div>
      </div>

      {/* Key Metrics */}
      <div className="key-metrics">
        <div className="metrics-grid">
          <div className="metric-item">
            <div className="metric-label-small">Strike</div>
            <div className="metric-value-large">{formatCurrency(strike)}</div>
          </div>
          <div className="metric-item">
            <div className="metric-label-small">Premium</div>
            <div className="metric-value-large">{formatCurrency(currentPremium)}</div>
          </div>
          <div className="metric-item">
            <div className="metric-label-small">Sim Premium</div>
            <div className="metric-value-large">{formatCurrency(simulatedPremium)}</div>
          </div>
          <div className="metric-item">
            <div className="metric-label-small">Volume</div>
            <div className="metric-value-large">{formatNumber(volume)}</div>
          </div>
        </div>
        <div className="metric-highlight">
          <div className="metric-label-small">
            Simulated Gain
            {loading && <span style={{ marginLeft: '8px', fontSize: '10px', color: '#666' }}>(updating...)</span>}
          </div>
          <div className={`gain-value ${gainClass}`} style={{ opacity: loading ? 0.5 : 1, transition: 'opacity 0.2s' }}>
            {formatPercentage(simulatedGain)}
          </div>
          {error && (
            <div style={{ fontSize: '10px', color: '#ff3939', marginTop: '4px', textAlign: 'center' }}>
              {error}
            </div>
          )}
        </div>
      </div>

      {/* Interactive Controls - Block swipe gestures in this area */}
      <div
        className="compact-controls"
        onTouchStart={(e) => e.stopPropagation()}
        onTouchMove={(e) => e.stopPropagation()}
        onTouchEnd={(e) => e.stopPropagation()}
      >
        <div className="control-row">
          <span className="control-label-inline">Days</span>
          <input
            type="range"
            className="control-slider-inline"
            min="1"
            max={maxDaysUntilExp}
            value={days}
            onChange={(e) => setDays(parseInt(e.target.value))}
            onTouchStart={(e) => e.stopPropagation()}
            onTouchMove={(e) => e.stopPropagation()}
            onMouseDown={(e) => e.stopPropagation()}
            style={{
              background: `linear-gradient(to right, #218aff 0%, #218aff ${
                (days / maxDaysUntilExp) * 100
              }%, #374151 ${(days / maxDaysUntilExp) * 100}%, #374151 100%)`
            }}
          />
          <span className="control-value-inline">{formatTargetDate(days)}</span>
        </div>
        <div className="control-row">
          <span className="control-label-inline">Move</span>
          <input
            type="range"
            className="control-slider-inline"
            min="-50"
            max="200"
            value={move}
            onChange={(e) => setMove(parseInt(e.target.value))}
            onTouchStart={(e) => e.stopPropagation()}
            onTouchMove={(e) => e.stopPropagation()}
            onMouseDown={(e) => e.stopPropagation()}
            style={{
              background: `linear-gradient(to right, #218aff 0%, #218aff ${
                ((move + 50) / 250) * 100
              }%, #374151 ${((move + 50) / 250) * 100}%, #374151 100%)`
            }}
          />
          <span className="control-value-inline">{formatGainLabel(move)}</span>
        </div>
        {/* Update Mode Toggle */}
        <div className="update-mode-toggle">
          <button
            className={`mode-btn ${updateMode === 'this' ? 'active' : ''}`}
            onClick={() => setUpdateMode('this')}
          >
            This Contract
          </button>
          <button
            className={`mode-btn ${updateMode === 'all' ? 'active' : ''}`}
            onClick={() => setUpdateMode('all')}
          >
            All Contracts
          </button>
        </div>

        {/* Action Buttons */}
        <div className="card-action-buttons">
          <button
            onClick={handleRecalculate}
            disabled={loading}
            className="refresh-data-btn"
            title={updateMode === 'all' ? 'Update all contracts with current settings' : 'Recalculate this contract with live market data'}
          >
            {loading ? 'Updating...' : (updateMode === 'all' ? 'Update All' : 'Refresh Data')}
          </button>
          <button
            onClick={handleReset}
            disabled={loading}
            className="reset-btn"
            title="Reset to original queue values"
          >
            Reset
          </button>
        </div>

        {/* Greeks Bar - inside controls section, above nav arrows */}
        <div className="greeks-bar">
          <div className="greek-item" title={getIvTooltip(contract) || undefined}>
            <div className="greek-label">IV</div>
            <div className="greek-value">{iv.toFixed(1)}%</div>
          </div>
          <div className="greek-item">
            <div className="greek-label">Δ</div>
            <div className="greek-value">{delta.toFixed(2)}</div>
          </div>
          <div className="greek-item">
            <div className="greek-label">Γ</div>
            <div className="greek-value">{gamma.toFixed(3)}</div>
          </div>
          <div className="greek-item">
            <div className="greek-label">Θ</div>
            <div className="greek-value">{theta.toFixed(2)}</div>
          </div>
          <div className="greek-item">
            <div className="greek-label">V</div>
            <div className="greek-value">{vega.toFixed(2)}</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContractCard;
